/**
 * `SiteShellSkeleton` — rendered inside the site-scoped routes of the
 * ViewRouter when `!activeSite && !loaded`, i.e. the first sites fetch
 * is still in flight (founder 2026-10-21).
 *
 * Before this component, the ViewRouter fell straight through to a
 * `<Phase2StubView reason="no_active_site" />` whenever `activeSite`
 * was null, which painted the loud "No active site · Pick a site from
 * the menu, or add a new one to continue." splash for ~1 round trip
 * on every page load before the SiteProvider's `sites.list()`
 * resolved. The splash is correct AFTER load (nobody has sites) but
 * dishonest during load (the merchant does have sites).
 *
 * The skeleton holds the shell at the max-w rhythm the real views use
 * (`max-w-7xl` / `max-w-3xl` family) with a quiet monochrome ladder.
 * No lime / amber / coral — this is a loading state, not a status.
 */

import { useC } from '../../lib/theme.js';

export function SiteShellSkeleton(): JSX.Element {
  const C = useC();
  const bar = (w: number, h = 10): React.CSSProperties => ({
    height: h,
    width: w,
    maxWidth: '100%',
    background: C.surface2,
    borderRadius: 2,
  });
  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10"
      aria-busy="true"
      aria-label="Loading"
      data-testid="site-shell-loading"
    >
      <div style={{ marginBottom: 24 }}>
        <div style={bar(120, 8)} />
        <div style={{ ...bar(360, 36), marginTop: 10 }} />
      </div>
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        style={{ marginBottom: 24 }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              minHeight: 160,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <div style={bar(80, 8)} />
            <div style={bar(140, 20)} />
            <div style={bar(100, 10)} />
          </div>
        ))}
      </div>
    </div>
  );
}
